import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AppRole, AppUser } from '../types';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

interface AuthContextValue {
  user: AppUser | null;
  loading: boolean;
  isSupabaseConfigured: boolean;
  isDemoModeEnabled: boolean;
  signIn(email: string, password: string): Promise<void>;
  signUp(displayName: string, email: string, password: string, accountType: 'parent' | 'teacher'): Promise<void>;
  signOut(): Promise<void>;
  signInDemo(role: AppRole): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const DEMO_KEY = 'quizpiko_demo_user';
const isDemoModeEnabled = import.meta.env.VITE_ENABLE_DEMO_MODE === 'true' && !isSupabaseConfigured;

async function loadAppUser(authUser: { id: string; email?: string | null }): Promise<AppUser> {
  const [{ data: profile }, { data: primaryRole }] = await Promise.all([
    supabase.from('profiles').select('display_name').eq('id', authUser.id).maybeSingle(),
    supabase.rpc('get_my_primary_role'),
  ]);

  const role = (primaryRole || 'parent') as AppRole;
  return {
    id: authUser.id,
    email: authUser.email || '',
    displayName: profile?.display_name || authUser.email?.split('@')[0] || 'QuizPiko User',
    role,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function initialize() {
      const demo = isDemoModeEnabled ? localStorage.getItem(DEMO_KEY) : null;
      if (!isDemoModeEnabled) localStorage.removeItem(DEMO_KEY);
      if (demo && !isSupabaseConfigured) {
        try {
          const parsed = JSON.parse(demo) as AppUser;
          if (active) setUser(parsed);
        } catch {
          localStorage.removeItem(DEMO_KEY);
        }
      }

      if (isSupabaseConfigured) {
        const { data } = await supabase.auth.getSession();
        if (data.session?.user && active) {
          setUser(await loadAppUser(data.session.user));
        }
      }
      if (active) setLoading(false);
    }

    void initialize();
    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!isSupabaseConfigured || !active) return;
      setUser(session?.user ? await loadAppUser(session.user) : null);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!isSupabaseConfigured) throw new Error('Supabase is not configured. Add the browser-safe staging environment values.');
    localStorage.removeItem(DEMO_KEY);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.user) setUser(await loadAppUser(data.user));
  }, []);

  const signUp = useCallback(async (
    displayName: string,
    email: string,
    password: string,
    accountType: 'parent' | 'teacher',
  ) => {
    if (!isSupabaseConfigured) throw new Error('Supabase is not configured.');
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName, account_type: accountType } },
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    localStorage.removeItem(DEMO_KEY);
    if (isSupabaseConfigured) await supabase.auth.signOut();
    setUser(null);
  }, []);

  const signInDemo = useCallback(async (role: AppRole) => {
    if (!isDemoModeEnabled) throw new Error('Demo mode is disabled while staging Supabase is connected.');
    if (isSupabaseConfigured) await supabase.auth.signOut();
    const demoUser: AppUser = {
      id: `demo-${role}`,
      email: `${role}@demo.quizpiko.local`,
      displayName: role.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()),
      role,
      demo: true,
    };
    localStorage.setItem(DEMO_KEY, JSON.stringify(demoUser));
    setUser(demoUser);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    isSupabaseConfigured,
    isDemoModeEnabled,
    signIn,
    signUp,
    signOut,
    signInDemo,
  }), [user, loading, signIn, signUp, signOut, signInDemo]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
